import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, ShieldCheck, Lock, XCircle, Copy, UserCheck, AlertTriangle, Syringe, BadgeCheck, Send } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

// Every consent on file for a patient — including the withdrawn and superseded ones, which are
// shown rather than filtered. F-9: a withdrawal creates a NEW record and the original stays
// exactly as it was. A list that hides them would misrepresent the record it is summarising.

const STATUS_TONE = {
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  online_signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  witnessed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  withdrawn: 'bg-red-50 text-red-700 border-red-200',
  superseded: 'bg-brand-surface text-brand-muted border-brand-border',
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
}

function ConsentCard({ c, onWithdraw, onWitness, onSedationStart, onVerify, onCopy }) {
  const [copied, setCopied] = useState(false)
  const link = c.token ? `${window.location.origin}/c/${c.token}` : null

  const copy = () => {
    navigator.clipboard?.writeText(link)
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={cn('rounded-xl border bg-white p-4',
      ['withdrawn', 'superseded'].includes(c.status) ? 'border-brand-border/60 opacity-75' : 'border-brand-border')}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold capitalize text-brand-ink">{c.kind} consent</span>
        <span className={cn('rounded border px-1.5 py-0.5 text-[11px] font-semibold', STATUS_TONE[c.status] || 'border-brand-border bg-brand-surface text-brand-muted')}>
          {c.status.replace('_', ' ')}
        </span>
        {c.procedure_class === 'surgical' && (
          <span className="rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-semibold text-orange-700">Surgical</span>
        )}
        {c.t3 && !c.witnessed_at && (
          <span className="inline-flex items-center gap-1 rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700">
            <AlertTriangle size={10} /> Needs a witnessed signature on the day
          </span>
        )}
        <span className="ml-auto font-mono text-[11px] text-brand-muted">{c.form_version}</span>
      </div>

      {c.description && <p className="mb-2 text-sm text-brand-ink">{c.description}</p>}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {c.signed_at && (
          <>
            <dt className="text-brand-muted">Signed</dt>
            <dd className="text-brand-ink">{c.signed_at} by {c.signed_name} ({c.signer_capacity}{c.signer_relationship ? ` · ${c.signer_relationship}` : ''})</dd>
          </>
        )}
        {c.co_signer_name && (<><dt className="text-brand-muted">Co-signed</dt><dd className="text-brand-ink">{c.co_signer_name}</dd></>)}
        {c.witnessed_at && (<><dt className="text-brand-muted">Witnessed</dt><dd className="text-brand-ink">{c.witnessed_at} · {c.witness_name} ({c.witness_role})</dd></>)}
        {c.signed_at && (
          <>
            <dt className="text-brand-muted">Identity checked</dt>
            <dd className={c.identity_verified_at ? 'text-brand-ink' : 'text-amber-700'}>
              {c.identity_verified_at
                ? `${c.identity_verified_at} by ${c.identity_verified_by} (${(c.identity_method || '').replace(/_/g, ' ')})`
                : 'not yet — check ID when they arrive'}
            </dd>
            <dt className="text-brand-muted">Patient's copy</dt>
            <dd className={c.copy_given_at ? 'text-brand-ink' : 'text-brand-muted'}>
              {c.copy_given_at ? `${c.copy_given_at} · ${c.copy_given_method}` : 'not given yet'}
            </dd>
          </>
        )}
        {c.sedation_started_at && (<><dt className="text-brand-muted">Sedation started</dt><dd className="text-brand-ink">{c.sedation_started_at} — after consent</dd></>)}
        {c.withdrawn_reason && (<><dt className="text-brand-muted">Withdrawn because</dt><dd className="text-red-700">{c.withdrawn_reason}</dd></>)}
        {c.supersedes_id && (<><dt className="text-brand-muted">Replaces</dt><dd className="text-brand-ink">consent #{c.supersedes_id}</dd></>)}
        {c.kind === 'treatment' && (
          <>
            <dt className="text-brand-muted">Photographs</dt>
            <dd className="text-brand-ink">
              {c.photo_clinical_record ? 'Clinical record' : 'not for the record'}
              {c.photo_marketing ? ' · website/social media' : ' · not for marketing'}
            </dd>
          </>
        )}
      </dl>

      <div className="mt-3 flex flex-wrap gap-2 border-t border-brand-border pt-3">
        {link && (
          <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-xs font-semibold text-brand-ink hover:bg-brand-surface">
            <Copy size={12} /> {copied ? 'Link copied' : 'Copy the patient’s link'}
          </button>
        )}
        {/* Sedation guidance: consent must be in place BEFORE any sedative is given, because a
            sedated patient cannot consent. Recording the start time here is what makes that
            ordering provable — and the server refuses to record it if consent is not complete. */}
        {c.kind === 'sedation' && c.complete && !c.sedation_started_at && (
          <button onClick={() => onSedationStart(c)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-800 hover:bg-orange-100">
            <Syringe size={12} /> Record sedation started
          </button>
        )}
        {c.t3 && c.signed_at && !c.witnessed_at && (
          <button onClick={() => onWitness(c)} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white hover:brightness-95">
            <UserCheck size={12} /> Record the witnessed signature
          </button>
        )}
        {c.signed_at && !c.identity_verified_at && (
          <button onClick={() => onVerify(c)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-xs font-semibold text-brand-primary hover:bg-brand-surface">
            <BadgeCheck size={12} /> Verify identity on arrival
          </button>
        )}
        {c.signed_at && !c.copy_given_at && (
          <button onClick={() => onCopy(c)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-xs font-semibold text-brand-ink hover:bg-brand-surface">
            <Send size={12} /> Record copy given
          </button>
        )}
        {!['withdrawn', 'superseded'].includes(c.status) && (
          <button onClick={() => onWithdraw(c)} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
            <XCircle size={12} /> Withdraw
          </button>
        )}
      </div>
    </div>
  )
}

export default function Consents({ patient = {}, consents = [] }) {
  const withdraw = (c) => {
    const reason = window.prompt('Why is this consent being withdrawn?\n\nThe original stays on the record — this creates a new entry recording the withdrawal.')
    if (!reason || !reason.trim()) return
    router.post(`/consents/${c.id}/withdraw`, { reason, patient_id: patient.id }, { preserveScroll: true })
  }
  const sedationStart = (c) => {
    if (!window.confirm('Record that the sedative is being given NOW?\n\nThis stamps the time on the record. It is refused if the sedation consent is not signed and witnessed.')) return
    router.post(`/consents/${c.id}/sedation-started`, { patient_id: patient.id }, { preserveScroll: true })
  }
  // Verified as a SEPARATE event: the original signature is never overwritten.
  const verify = (c) => {
    const how = window.prompt('How was identity checked?\n\nType: id (photo ID in person), whatsapp (OTP), email (OTP)', 'id')
    if (!how) return
    const map = { id: 'in_person_id', whatsapp: 'otp_whatsapp', email: 'otp_email' }
    router.post(`/signatures/Consent/${c.id}/verify-identity`,
      { identity_method: map[how.trim().toLowerCase()] || 'in_person_id', patient_id: patient.id },
      { preserveScroll: true })
  }
  const copyGiven = (c) => {
    const how = window.prompt('How did the patient get their copy?\n\nwhatsapp / email / printed / collected', 'whatsapp')
    if (!how) return
    router.post(`/signatures/Consent/${c.id}/copy-given`,
      { copy_method: how.trim().toLowerCase(), patient_id: patient.id }, { preserveScroll: true })
  }
  const witness = (c) => {
    const name = window.prompt('Who witnessed the signature? (full name)')
    if (!name || !name.trim()) return
    const role = window.prompt('Their role (e.g. dental assistant, reception)') || ''
    router.post(`/consents/${c.id}/witness`, { witness_name: name, witness_role: role, patient_id: patient.id }, { preserveScroll: true })
  }

  const live = consents.filter((c) => !['withdrawn', 'superseded'].includes(c.status))
  const past = consents.filter((c) => ['withdrawn', 'superseded'].includes(c.status))

  return (
    <DashboardLayout>
      <Link href={`/patients/${patient.id}/file`} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> {patient.name} — digital file
      </Link>

      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <ShieldCheck size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Consent — {patient.name}</h1>
          <p className="text-sm text-brand-muted">What was agreed, by whom, and when.</p>
        </div>
      </div>

      {consents.length === 0 && (
        <p className="rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-muted">
          Nothing on file yet. A treatment-plan consent is created from an estimate — open the estimate
          and use “Send consent form”.
        </p>
      )}

      <div className="space-y-3">
        {live.map((c) => <ConsentCard key={c.id} c={c} onWithdraw={withdraw} onWitness={witness} onSedationStart={sedationStart} onVerify={verify} onCopy={copyGiven} />)}
      </div>

      {past.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
            <Lock size={14} className="text-brand-muted" /> Withdrawn and replaced ({past.length})
          </h2>
          <p className="mb-2 text-xs text-brand-muted">
            Kept, never deleted — a record that hides its own corrections is worth less than one that shows them.
          </p>
          <div className="space-y-3">
            {past.map((c) => <ConsentCard key={c.id} c={c} onWithdraw={withdraw} onWitness={witness} onSedationStart={sedationStart} onVerify={verify} onCopy={copyGiven} />)}
          </div>
        </>
      )}
    </DashboardLayout>
  )
}
